import type { QueryClient } from '@tanstack/react-query';

// Async mutations that route through Tier 4 → n8n workflows take 2-3s
// in the common case, up to 25-30s under burst. An immediate
// invalidateQueries races the workflow's DB write — UI reads stale
// rows and overwrites any optimistic local patch the caller applied.
// Defer reconciliation past the typical SLA so the workflow has time
// to land before we re-read.

export const WF_RECONCILE_DELAY_MS = 3000;

export function scheduleInvalidate(
  qc: QueryClient,
  queryKey: readonly unknown[],
  delayMs: number = WF_RECONCILE_DELAY_MS,
): void {
  setTimeout(() => {
    qc.invalidateQueries({ queryKey });
  }, delayMs);
}

// Patch every cached array under `rootKey` (e.g. TASK_KEYS.all) and
// return a snapshot the caller can use to roll back on mutation error.
// History pagination + non-array shapes are left alone — they reconcile
// on the delayed invalidation.
export type Snapshot = Array<[readonly unknown[], unknown]>;

export function patchArrayCaches<T extends { [k: string]: unknown }>(
  qc: QueryClient,
  rootKey: readonly unknown[],
  patch: (row: T) => T | null,
): Snapshot {
  const snapshot: Snapshot = [];
  qc.getQueriesData<T[]>({ queryKey: rootKey }).forEach(([key, data]) => {
    if (!Array.isArray(data)) return;
    snapshot.push([key, data]);
    const next = data
      .map(patch)
      .filter((row): row is T => row !== null);
    qc.setQueryData(key, next);
  });
  return snapshot;
}

export function rollbackSnapshot(qc: QueryClient, snapshot: Snapshot | undefined): void {
  if (!snapshot) return;
  snapshot.forEach(([key, data]) => qc.setQueryData(key, data));
}
